import React, { useState } from "react";
import { trpc } from "../lib/trpc.js";
import {
  Building2, Users, UserPlus, Copy, Check, Clock, Shield,
  Crown, Trash2, RefreshCw, Link2, AlertCircle, Mail,
  ChevronDown, Loader2,
} from "lucide-react";

export function Organization() {
  const [activeTab, setActiveTab] = useState<"members" | "invites">("members");

  const tabs = [
    { id: "members" as const, label: "Membros", icon: Users },
    { id: "invites" as const, label: "Convites", icon: UserPlus },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
          <Building2 className="w-6 h-6 text-primary" />
          Organização
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie membros da equipe e convites
        </p>
      </div>

      <OrgInfo />

      <div className="flex items-center gap-1 p-1 bg-secondary/50 border border-border rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-card text-foreground shadow-sm border border-border"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? "text-primary" : ""}`} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "members" && <MembersTab />}
      {activeTab === "invites" && <InvitesTab />}
    </div>
  );
}

function OrgInfo() {
  const { data: org, isLoading } = trpc.organization.getCurrent.useQuery();

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-xl p-5 animate-pulse">
        <div className="h-5 bg-muted rounded w-1/3 mb-2" />
        <div className="h-3 bg-muted rounded w-1/2" />
      </div>
    );
  }

  if (!org) return null;

  const planColors: Record<string, string> = {
    free: "bg-muted text-muted-foreground",
    starter: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    pro: "bg-primary/10 text-primary border-primary/20",
    enterprise: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{org.name}</h2>
            <p className="text-xs text-muted-foreground">/{org.slug}</p>
          </div>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full border font-medium capitalize ${planColors[org.plan] || planColors.free}`}>
          {org.plan}
        </span>
      </div>
    </div>
  );
}

function MembersTab() {
  const { data: members, isLoading, refetch } = trpc.organization.listMembers.useQuery();
  const updateRole = trpc.organization.updateMemberRole.useMutation({
    onSuccess: () => refetch(),
  });
  const removeMember = trpc.organization.removeMember.useMutation({
    onSuccess: () => refetch(),
  });

  const roleIcons: Record<string, typeof Shield> = {
    owner: Crown,
    admin: Shield,
    member: Users,
    viewer: Users,
  };

  const roleColors: Record<string, string> = {
    owner: "text-yellow-500 bg-yellow-500/10",
    admin: "text-primary bg-primary/10",
    member: "text-muted-foreground bg-muted",
    viewer: "text-muted-foreground/60 bg-muted/50",
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-muted rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-1/3 mb-1" />
                <div className="h-3 bg-muted rounded w-1/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{members?.length || 0} membro{(members?.length || 0) !== 1 ? "s" : ""}</p>
        <button onClick={() => refetch()} className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary/50 transition-colors">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {members?.map((member) => {
        const RoleIcon = roleIcons[member.role] || Users;
        return (
          <div key={member.id} className="bg-card border border-border rounded-xl p-4 hover:border-primary/10 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                {(member.name || member.email)?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{member.name || "Sem nome"}</p>
                <p className="text-xs text-muted-foreground truncate">{member.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium ${roleColors[member.role] || ""}`}>
                  <RoleIcon className="w-3 h-3" />
                  {member.role}
                </span>
                {member.lastLoginAt && (
                  <span className="text-[10px] text-muted-foreground/60 hidden sm:flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />
                    {new Date(member.lastLoginAt).toLocaleDateString("pt-BR")}
                  </span>
                )}
                {member.role !== "owner" && (
                  <button
                    onClick={() => {
                      if (confirm("Remover este membro?")) {
                        removeMember.mutate({ userId: member.id });
                      }
                    }}
                    className="p-1.5 text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InvitesTab() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member" | "viewer">("member");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  const { data: invites, isLoading, refetch } = trpc.organization.listInvites.useQuery();
  const createInvite = trpc.organization.createInvite.useMutation({
    onSuccess: (data) => {
      refetch();
      setEmail("");
      // Auto-copy the invite link
      const fullUrl = `${window.location.origin}${data.inviteUrl}`;
      navigator.clipboard.writeText(fullUrl);
      setCopiedId(data.id);
      setTimeout(() => setCopiedId(null), 3000);
    },
  });
  const revokeInvite = trpc.organization.revokeInvite.useMutation({
    onSuccess: () => refetch(),
  });

  const handleCreateInvite = (e: React.FormEvent) => {
    e.preventDefault();
    createInvite.mutate({
      email: email || undefined,
      role,
    });
  };

  const copyInviteLink = (inviteCode: string, id: string) => {
    const url = `${window.location.origin}/register?invite=${inviteCode}`;
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 3000);
  };

  const statusColors: Record<string, string> = {
    pending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
    accepted: "bg-success/10 text-success border-success/20",
    expired: "bg-muted text-muted-foreground border-border",
    revoked: "bg-destructive/10 text-destructive border-destructive/20",
  };

  const statusLabels: Record<string, string> = {
    pending: "Pendente",
    accepted: "Aceito",
    expired: "Expirado",
    revoked: "Revogado",
  };

  return (
    <div className="space-y-4">
      {/* Create Invite Form */}
      <form onSubmit={handleCreateInvite} className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Convidar Membro</h3>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground mb-1 block">Email (opcional)</label>
            <div className="flex items-center gap-2 bg-background border border-input rounded-lg px-3 py-2 focus-within:border-primary/40 transition-colors">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.com (ou deixe vazio para link genérico)"
                className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/60"
              />
            </div>
          </div>

          <div className="relative">
            <label className="text-xs text-muted-foreground mb-1 block">Papel</label>
            <button
              type="button"
              onClick={() => setShowRoleDropdown(!showRoleDropdown)}
              className="flex items-center gap-2 bg-background border border-input rounded-lg px-3 py-2 text-sm text-foreground hover:border-primary/40 transition-colors min-w-[120px]"
            >
              <Shield className="w-3.5 h-3.5 text-primary" />
              <span className="capitalize">{role}</span>
              <ChevronDown className="w-3 h-3 ml-auto text-muted-foreground" />
            </button>
            {showRoleDropdown && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-10 py-1">
                {(["admin", "member", "viewer"] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => { setRole(r); setShowRoleDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary/50 transition-colors capitalize ${role === r ? "text-primary font-medium" : "text-foreground"}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="self-end">
            <button
              type="submit"
              disabled={createInvite.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 shadow-md shadow-primary/20"
            >
              {createInvite.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Gerar Link
            </button>
          </div>
        </div>

        {createInvite.error && (
          <p className="text-xs text-destructive flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />{createInvite.error.message}
          </p>
        )}

        {createInvite.data && (
          <div className="flex items-center gap-2 p-3 bg-success/5 border border-success/20 rounded-lg">
            <Check className="w-4 h-4 text-success shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-success font-medium">Convite criado e link copiado!</p>
              <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                {window.location.origin}{createInvite.data.inviteUrl}
              </p>
            </div>
          </div>
        )}
      </form>

      {/* Invites List */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Convites</h3>
          <button onClick={() => refetch()} className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary/50 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse border border-border rounded-lg p-3">
                <div className="h-3 bg-muted rounded w-2/3 mb-1" />
                <div className="h-2 bg-muted rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : invites && invites.length > 0 ? (
          <div className="space-y-2">
            {invites.map((invite) => (
              <div key={invite.id} className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-secondary/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-foreground truncate">
                      {invite.email || "Link genérico"}
                    </p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${statusColors[invite.status] || ""}`}>
                      {statusLabels[invite.status] || invite.status}
                    </span>
                    <span className="text-[10px] text-muted-foreground capitalize">{invite.role}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Expira: {new Date(invite.expiresAt).toLocaleDateString("pt-BR")}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {invite.status === "pending" && (
                    <>
                      <button
                        onClick={() => copyInviteLink(invite.inviteCode, invite.id)}
                        className="p-1.5 text-muted-foreground hover:text-primary rounded-lg hover:bg-primary/10 transition-colors"
                        title="Copiar link"
                      >
                        {copiedId === invite.id ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => revokeInvite.mutate({ inviteId: invite.id })}
                        className="p-1.5 text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
                        title="Revogar convite"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <UserPlus className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
            <p className="text-sm text-muted-foreground">Nenhum convite criado</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Crie um convite acima para adicionar membros</p>
          </div>
        )}
      </div>
    </div>
  );
}
